import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import { GoogleMap, LoadScript, Marker } from '@react-google-maps/api';
import { MapPin, GraduationCap, Star, Clock, IndianRupee, Mail, Phone, Unlock, Coins, ArrowLeft, BookOpen, Award, Trophy, MessageSquare, ThumbsUp, Languages, Video, User, Calendar, CheckCircle } from 'lucide-react';
import ShareButton from '../../../components/ShareButton';
import apiClient from '../../../services/apiClient';
import { toast } from 'sonner';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const mapContainerStyle = { width: '100%', height: '350px', borderRadius: '8px' };
const libraries = ['places'];

// Helper component for star ratings
const StarRating = ({ rating, size = 'default', showNumber = true }) => {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    stars.push(
      <Star
        key={i}
        className={`${size === 'small' ? 'h-3 w-3' : 'h-4 w-4'
          } ${i <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
          }`}
      />
    );
  }
  return (
    <div className="flex items-center gap-1">
      {stars}
      {showNumber && <span className="ml-1 text-sm font-medium">{rating.toFixed(1)}</span>}
    </div>
  );
};

// Video embed component
const VideoPlayer = ({ url }) => {
  if (!url) return null;

  const getEmbedUrl = (url) => {
    // YouTube
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      const videoId = url.includes('youtu.be')
        ? url.split('youtu.be/')[1]?.split('?')[0]
        : url.split('v=')[1]?.split('&')[0];
      return `https://www.youtube.com/embed/${videoId}`;
    }
    // Google Drive
    if (url.includes('drive.google.com')) {
      const fileId = url.match(/[-\w]{25,}/);
      return fileId ? `https://drive.google.com/file/d/${fileId[0]}/preview` : null;
    }
    return null;
  };

  const embedUrl = getEmbedUrl(url);

  if (!embedUrl) {
    return (
      <Alert>
        <AlertDescription>
          Video link provided but cannot be embedded. <a href={url} target="_blank" rel="noopener noreferrer" className="underline">Open in new tab</a>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="relative pb-[56.25%] h-0 overflow-hidden rounded-lg">
      <iframe
        src={embedUrl}
        className="absolute top-0 left-0 w-full h-full"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        title="Intro Video"
      />
    </div>
  );
};

export const TutorProfileDetail = () => {
  const { tutorId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [tutor, setTutor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unlocking, setUnlocking] = useState(false);
  const [wallet, setWallet] = useState({ balance: 0 });
  const [contactUnlocked, setContactUnlocked] = useState(false);
  const [contactDetails, setContactDetails] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [ratingsSummary, setRatingsSummary] = useState(null);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewData, setReviewData] = useState({
    rating: 5,
    review_text: '',
    teaching_quality: 5,
    communication: 5,
    punctuality: 5
  });

  useEffect(() => {
    fetchTutorProfile();
    fetchReviews();
    fetchRatingsSummary();
    if (token) {
      fetchWallet();
      checkPurchaseStatus();
    }
  }, [tutorId, token]);

  const fetchTutorProfile = async () => {
    try {
      const data = await apiClient.get(`/tutors/${tutorId}`);
      setTutor(data);
    } catch (error) {
      toast.error('Failed to load tutor profile');
    } finally {
      setLoading(false);
    }
  };

  const fetchWallet = async () => {
    try {
      const data = await apiClient.get('/wallet/balance');
      setWallet({ balance: data.balance || 0 });
    } catch (error) {
      console.error('Failed to fetch wallet');
    }
  };

  const checkPurchaseStatus = async () => {
    try {
      const data = await apiClient.get(
        `/check-purchase-status?target_type=tutor_profile&target_id=${tutorId}`
      );
      if (data.purchased) {
        setContactUnlocked(true);
        setContactDetails(data.contact_details);
      }
    } catch (error) {
      console.error('Failed to check purchase status');
    }
  };

  const fetchReviews = async () => {
    try {
      const data = await apiClient.get(`/tutors/${tutorId}/reviews?limit=10`);
      setReviews(data.reviews || []);
    } catch (error) {
      console.error('Failed to fetch reviews');
    }
  };

  const fetchRatingsSummary = async () => {
    try {
      const data = await apiClient.get(`/tutors/${tutorId}/ratings-summary`);
      setRatingsSummary(data);
    } catch (error) {
      console.error('Failed to fetch ratings summary');
    }
  };

  const submitReview = async () => {
    if (!token) {
      toast.error('Please login to submit a review');
      return;
    }

    if (user?.role !== 'student') {
      toast.error('Only students can submit reviews');
      return;
    }

    setReviewsLoading(true);
    try {
      await apiClient.post(
        `/tutors/${tutorId}/reviews`,
        reviewData
      );

      toast.success('Review submitted successfully!');
      setShowReviewForm(false);
      setReviewData({
        rating: 5,
        review_text: '',
        teaching_quality: 5,
        communication: 5,
        punctuality: 5
      });
      fetchReviews();
      fetchRatingsSummary();
      fetchTutorProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setReviewsLoading(false);
    }
  };

  const handleUnlock = async () => {
    if (!token) {
      toast.error('Please login to unlock contact');
      navigate('/login');
      return;
    }

    if (wallet.balance < tutor.unlock_price) {
      toast.error(`Insufficient balance. Need ${tutor.unlock_price} coins`);
      return;
    }

    setUnlocking(true);
    try {
      const data = await apiClient.post(
        `/unlock-contact?target_type=tutor_profile&target_id=${tutorId}`
      );

      toast.success(`Contact unlocked! ${data.coins_deducted} coins deducted`);
      setContactUnlocked(true);
      setContactDetails(data.contact_details);
      fetchWallet();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unlock contact');
    } finally {
      setUnlocking(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-600">Loading tutor profile...</p>
      </div>
    );
  }

  if (!tutor) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-600">Tutor not found</p>
      </div>
    );
  }

  const mapCenter = tutor.latitude && tutor.longitude ? { lat: tutor.latitude, lng: tutor.longitude } : { lat: 28.6139, lng: 77.2090 };
  const isApproved = tutor.review_status === 'approved';

  return (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-8">
      <div className="max-w-6xl mx-auto px-3 sm:px-6 lg:px-8">
        <Button variant="outline" onClick={() => navigate('/find-tutor')} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Tutors
        </Button>

        <div className="grid lg:grid-cols-3 gap-4 sm:gap-6">
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            {/* Main Profile Card */}
            <Card>
              <CardContent className="pt-6 p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row items-center sm:items-start space-y-4 sm:space-y-0 sm:space-x-6">
                  {tutor.photo_url ? (
                    <img src={tutor.photo_url} alt={tutor.full_name} className="w-24 h-24 sm:w-32 sm:h-32 rounded-full object-cover border-4 border-purple-100 flex-shrink-0" />
                  ) : (
                    <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <GraduationCap className="h-12 w-12 sm:h-16 sm:w-16 text-purple-600" />
                    </div>
                  )}
                  <div className="flex-1 text-center sm:text-left w-full">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex-1">
                        <h1 className="text-xl sm:text-3xl font-bold text-gray-900">{tutor.full_name || tutor.name}</h1>
                        <p className="text-lg text-gray-600">{tutor.qualification}</p>
                      </div>
                      <ShareButton
                        title={tutor.full_name || tutor.name}
                        text={`Check out ${tutor.full_name}'s profile - Expert in ${tutor.subjects?.slice(0, 2).join(', ')} with ${tutor.experience_years}+ years experience`}
                        variant="outline"
                        size="default"
                      />
                    </div>
                    <div className="flex gap-2 mt-3 flex-wrap">
                      {isApproved && <Badge className="bg-green-500">Verified</Badge>}
                      <Badge variant="outline">{tutor.experience_years}+ years exp</Badge>
                      {tutor.gender && <Badge variant="outline"><User className="h-3 w-3 mr-1" />{tutor.gender}</Badge>}
                      {tutor.city && <Badge variant="outline"><MapPin className="h-3 w-3 mr-1" />{tutor.city}</Badge>}
                      {tutor.rating > 0 && (
                        <Badge variant="outline" className="flex items-center">
                          <Star className="h-3 w-3 mr-1 fill-yellow-400 text-yellow-400" />
                          {tutor.rating} ({tutor.total_reviews || 0} reviews)
                        </Badge>
                      )}
                    </div>
                    <div className="mt-4 flex gap-3 items-center">
                      {tutor.hourly_rate && (
                        <div className="text-2xl font-bold text-purple-600">
                          ₹{tutor.hourly_rate}/hour
                        </div>
                      )}
                      {tutor.monthly_rate && (
                        <div className="text-2xl font-bold text-green-600">
                          ₹{tutor.monthly_rate}/month
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t">
                  <h3 className="font-semibold text-gray-700 mb-2">About</h3>
                  <p className="text-gray-600">{tutor.bio}</p>
                </div>

                {/* Subjects & Teaching Modes */}
                <div className="mt-6 grid md:grid-cols-2 gap-4">
                  <div>
                    <h3 className="font-semibold text-gray-700 mb-2 flex items-center">
                      <BookOpen className="h-4 w-4 mr-2" />Subjects
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {tutor.subjects?.map((subject, idx) => (
                        <Badge key={idx} variant="secondary">{subject}</Badge>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-700 mb-2">Teaching Modes</h3>
                    <div className="flex flex-wrap gap-2">
                      {tutor.teaching_mode?.map((mode, idx) => (
                        <Badge key={idx} variant="outline">{mode.replace('_', ' ')}</Badge>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Spoken Languages */}
                {tutor.spoken_languages && tutor.spoken_languages.length > 0 && (
                  <div className="mt-6">
                    <h3 className="font-semibold text-gray-700 mb-2 flex items-center">
                      <Languages className="h-4 w-4 mr-2" />Spoken Languages
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {tutor.spoken_languages.map((lang, idx) => (
                        <Badge key={idx} className="bg-blue-100 text-blue-700">{lang}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Teaching Categories */}
                {tutor.teaching_category && tutor.teaching_category.length > 0 && (
                  <div className="mt-6">
                    <h3 className="font-semibold text-gray-700 mb-2">Teaching Categories</h3>
                    <div className="flex flex-wrap gap-2">
                      {tutor.teaching_category.map((cat, idx) => (
                        <Badge key={idx} variant="secondary">{cat}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Intro Video */}
            {tutor.intro_video_url && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Video className="h-5 w-5 mr-2" />
                    Introduction Video
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <VideoPlayer url={tutor.intro_video_url} />
                </CardContent>
              </Card>
            )}

            {/* Educational History */}
            {tutor.educational_history && tutor.educational_history.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <GraduationCap className="h-5 w-5 mr-2" />
                    Educational History
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {tutor.educational_history.map((edu, idx) => (
                      <div key={idx} className="flex items-start space-x-3 border-l-2 border-purple-500 pl-4">
                        <Calendar className="h-5 w-5 text-purple-600 mt-1" />
                        <div>
                          <h4 className="font-semibold">{edu.degree}</h4>
                          <p className="text-gray-600">{edu.institution}</p>
                          <p className="text-sm text-gray-500">{edu.year}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Achievements */}
            {tutor.achievements && tutor.achievements.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Trophy className="h-5 w-5 mr-2" />
                    Achievements
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {tutor.achievements.map((achievement, idx) => (
                      <li key={idx} className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                        <span className="text-gray-700">{achievement}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Awards */}
            {tutor.awards && tutor.awards.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Award className="h-5 w-5 mr-2" />
                    Awards & Recognition
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {tutor.awards.map((award, idx) => (
                      <Badge key={idx} className="bg-yellow-100 text-yellow-800 px-3 py-1">
                        🏆 {award}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Reviews & Ratings Section */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center">
                    <MessageSquare className="h-5 w-5 mr-2" />
                    Reviews & Ratings
                  </CardTitle>
                  {token && user?.role === 'student' && (
                    <Button onClick={() => setShowReviewForm(!showReviewForm)} size="sm">
                      Write Review
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {/* Rating Summary */}
                {ratingsSummary && ratingsSummary.total_reviews > 0 && (
                  <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <div className="text-4xl font-bold text-gray-900">{ratingsSummary.average_rating}</div>
                        <StarRating rating={ratingsSummary.average_rating} showNumber={false} />
                        <p className="text-sm text-gray-600 mt-1">{ratingsSummary.total_reviews} reviews</p>
                      </div>
                      <div className="flex-1 max-w-md ml-6">
                        {[5, 4, 3, 2, 1].map(star => {
                          const count = ratingsSummary.rating_distribution[star] || 0;
                          const percentage = (count / ratingsSummary.total_reviews) * 100;
                          return (
                            <div key={star} className="flex items-center gap-2 mb-1">
                              <span className="text-sm w-3">{star}</span>
                              <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                              <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div className="h-full bg-yellow-400" style={{ width: `${percentage}%` }} />
                              </div>
                              <span className="text-xs text-gray-600 w-8 text-right">{count}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {/* Write Review Form */}
                {showReviewForm && (
                  <Card className="mb-6 border-2 border-purple-200">
                    <CardContent className="pt-6">
                      <h4 className="font-semibold mb-4">Write Your Review</h4>
                      <div className="space-y-4">
                        <div>
                          <Label>Overall Rating *</Label>
                          <div className="flex gap-2 mt-2">
                            {[1, 2, 3, 4, 5].map(star => (
                              <Star
                                key={star}
                                className={`h-8 w-8 cursor-pointer ${star <= reviewData.rating
                                    ? 'fill-yellow-400 text-yellow-400'
                                    : 'text-gray-300'
                                  }`}
                                onClick={() => setReviewData({ ...reviewData, rating: star })}
                              />
                            ))}
                          </div>
                        </div>

                        <div className="grid md:grid-cols-3 gap-4">
                          <div>
                            <Label>Teaching Quality</Label>
                            <select
                              className="w-full mt-1 px-3 py-2 border rounded-md"
                              value={reviewData.teaching_quality}
                              onChange={(e) => setReviewData({ ...reviewData, teaching_quality: parseInt(e.target.value) })}
                            >
                              {[5, 4, 3, 2, 1].map(val => <option key={val} value={val}>{val} Star{val > 1 ? 's' : ''}</option>)}
                            </select>
                          </div>
                          <div>
                            <Label>Communication</Label>
                            <select
                              className="w-full mt-1 px-3 py-2 border rounded-md"
                              value={reviewData.communication}
                              onChange={(e) => setReviewData({ ...reviewData, communication: parseInt(e.target.value) })}
                            >
                              {[5, 4, 3, 2, 1].map(val => <option key={val} value={val}>{val} Star{val > 1 ? 's' : ''}</option>)}
                            </select>
                          </div>
                          <div>
                            <Label>Punctuality</Label>
                            <select
                              className="w-full mt-1 px-3 py-2 border rounded-md"
                              value={reviewData.punctuality}
                              onChange={(e) => setReviewData({ ...reviewData, punctuality: parseInt(e.target.value) })}
                            >
                              {[5, 4, 3, 2, 1].map(val => <option key={val} value={val}>{val} Star{val > 1 ? 's' : ''}</option>)}
                            </select>
                          </div>
                        </div>

                        <div>
                          <Label>Your Review</Label>
                          <Textarea
                            placeholder="Share your experience with this tutor..."
                            value={reviewData.review_text}
                            onChange={(e) => setReviewData({ ...reviewData, review_text: e.target.value })}
                            rows={4}
                          />
                        </div>

                        <div className="flex gap-2">
                          <Button onClick={submitReview} disabled={reviewsLoading}>
                            {reviewsLoading ? 'Submitting...' : 'Submit Review'}
                          </Button>
                          <Button variant="outline" onClick={() => setShowReviewForm(false)}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Reviews List */}
                <div className="space-y-4">
                  {reviews.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">No reviews yet. Be the first to review!</p>
                  ) : (
                    reviews.map((review, idx) => (
                      <Card key={idx} className="border-l-4 border-l-purple-500">
                        <CardContent className="pt-4">
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-semibold">{review.student_name}</span>
                                {review.is_verified_purchase && (
                                  <Badge className="bg-green-100 text-green-700 text-xs">
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    Verified
                                  </Badge>
                                )}
                              </div>
                              <StarRating rating={review.rating} size="small" showNumber={false} />
                            </div>
                            <span className="text-sm text-gray-500">{review.posted_ago}</span>
                          </div>
                          {review.review_text && (
                            <p className="mt-3 text-gray-700">{review.review_text}</p>
                          )}
                          {(review.teaching_quality || review.communication || review.punctuality) && (
                            <div className="flex gap-4 mt-3 text-sm">
                              {review.teaching_quality && (
                                <span className="text-gray-600">Teaching: {review.teaching_quality}⭐</span>
                              )}
                              {review.communication && (
                                <span className="text-gray-600">Communication: {review.communication}⭐</span>
                              )}
                              {review.punctuality && (
                                <span className="text-gray-600">Punctuality: {review.punctuality}⭐</span>
                              )}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Map */}
            {GOOGLE_MAPS_API_KEY && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <MapPin className="h-5 w-5 mr-2" />
                    Location
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY} libraries={libraries}>
                    <GoogleMap mapContainerStyle={mapContainerStyle} center={mapCenter} zoom={13}>
                      <Marker position={mapCenter} />
                    </GoogleMap>
                  </LoadScript>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar - Contact & Unlock */}
          <div className="lg:col-span-1">
            <div className="sticky top-6 space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Unlock className="h-5 w-5 mr-2" />
                    Contact Information
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {contactUnlocked ? (
                    <div className="space-y-3">
                      <Alert className="bg-green-50 border-green-200">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <AlertDescription className="text-green-700">
                          Contact unlocked successfully!
                        </AlertDescription>
                      </Alert>
                      {contactDetails?.email && (
                        <div className="flex items-center space-x-2">
                          <Mail className="h-4 w-4 text-gray-500" />
                          <a href={`mailto:${contactDetails.email}`} className="text-blue-600 hover:underline">
                            {contactDetails.email}
                          </a>
                        </div>
                      )}
                      {contactDetails?.phone && (
                        <div className="flex items-center space-x-2">
                          <Phone className="h-4 w-4 text-gray-500" />
                          <a href={`tel:${contactDetails.phone}`} className="text-blue-600 hover:underline">
                            {contactDetails.phone}
                          </a>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div>
                      <p className="text-sm text-gray-600 mb-4">
                        Unlock contact details to connect with this tutor
                      </p>
                      <div className="flex items-center justify-between mb-4 p-3 bg-purple-50 rounded-lg">
                        <span className="text-sm font-medium">Price:</span>
                        <span className="flex items-center font-bold text-purple-600">
                          <Coins className="h-4 w-4 mr-1" />
                          {tutor.unlock_price} coins
                        </span>
                      </div>
                      {token && (
                        <div className="mb-4">
                          <p className="text-sm text-gray-600">Your balance: <span className="font-semibold">{wallet.balance} coins</span></p>
                        </div>
                      )}
                      <Button
                        onClick={handleUnlock}
                        disabled={unlocking || !isApproved}
                        className="w-full"
                      >
                        {unlocking ? 'Unlocking...' : `Unlock for ${tutor.unlock_price} coins`}
                      </Button>
                      {!isApproved && (
                        <Alert className="mt-3">
                          <AlertDescription>
                            This tutor profile is under review
                          </AlertDescription>
                        </Alert>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Quick Stats */}
              <Card>
                <CardHeader>
                  <CardTitle>Quick Stats</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Experience:</span>
                    <span className="font-semibold">{tutor.experience_years}+ years</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Students:</span>
                    <span className="font-semibold">{tutor.total_students || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Reviews:</span>
                    <span className="font-semibold">{tutor.total_reviews || 0}</span>
                  </div>
                  {tutor.rating > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Rating:</span>
                      <StarRating rating={tutor.rating} size="small" />
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TutorProfileDetail;
